import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, usePropertyAddressUrl, usePropertyField, useCollectionBinding } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
// Full site page metadata for building navigation. This repo's own
// `pages.json` only carries the section overrides for a few pages, so it lacks
// the name/path/position fields the nav needs; this manifest mirrors the
// resolved page set (matching the inherited header's static page list).
import pagesData from '../header-nav-pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================
// Navigation is built dynamically from pages.json, respecting each page's
// "visible" flag and "position" ordering. This ensures that toggling a page's
// visibility in the CMS correctly shows/hides it in navigation.
// Pages like Home, Privacy Policy, Sitemap, etc. are excluded since they are
// not standard navigation items.
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
}

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position.
 * Pass `hasVirtualTours: false` to suppress the /virtual-tour link.
 */
function buildNavLinks(hasVirtualTours: boolean): NavLink[] {
  return Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false; // Exclude template pages (e.g. hyperlocal)
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([, page]) => ({
      label: page.name,
      href: page.path,
    }));
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/resident-access',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressGroupDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: {
      label: 'Map URL',
      type: 'url',
      // URL has no defaultField — defaults to usePropertyAddressUrl() at render
      // time. Editor overrides still win.
      defaultValue: '',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link Component
// ============================================================================

function DesktopNavLink({ link }: { link: NavLink }) {
  return (
    <a
      href={link.href}
      className="font-body text-[13px] font-semibold tracking-wider uppercase text-black hover:text-neutral-600 transition-colors py-2 whitespace-nowrap"
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================
// Measures available space and groups overflow items into a "More" dropdown.
// Uses a hidden measurement row to determine each link's natural width,
// then calculates how many links fit before the CTA button.
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Measure how many links fit in the available space
  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    // Read computed gap from the nav container to stay in sync with Tailwind responsive gap
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90; // approximate width of "More ▾" button

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      // If this isn't the last item, reserve space for the "More" button
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        // Check: can ALL remaining items fit without the "More" button?
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;

      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();

    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row — renders all links to measure natural widths */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href}
            className="font-body text-[13px] font-semibold tracking-wider uppercase"
          >
            {link.label}
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-end gap-5 xl:gap-7 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.href} link={link} />
        ))}

        {overflowLinks.length > 0 && (
          <div ref={moreRef} className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className="font-body text-[13px] font-semibold tracking-wider uppercase text-black hover:text-neutral-600 transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            <div
              className={`absolute right-0 top-full mt-1 bg-white border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
            >
              {overflowLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-black hover:text-neutral-600 hover:bg-[var(--vibe-color-surface)] transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link Component
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
}: {
  link: NavLink;
  onNavigate: () => void;
}) {
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className="block py-2.5 px-6 font-body text-[13px] font-semibold uppercase tracking-wider text-black hover:text-neutral-600 transition-colors border-b border-[var(--vibe-color-border)]/50"
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  // True while the user is actively dragging the window edge — we suppress
  // CSS transitions so the header snaps to the new banner height instantly.
  const [isResizing, setIsResizing] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Check whether the property has any virtual tours so we can hide the nav link
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Build navigation links dynamically from pages.json
  const navLinks = useMemo(() => buildNavLinks(hasVirtualTours), [hasVirtualTours]);

  const phone = useContentToken('header.phone');
  const address = useSlotGroup('header.address', addressGroupDef);

  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  // If the resolved logo matches the mono-white variant, swap to mono-black
  // so it's visible on the white nav background.
  const logoImage = (logoRaw.image && monoWhiteLogo && logoRaw.image === monoWhiteLogo && monoBlackLogo)
    ? monoBlackLogo
    : logoRaw.image;
  const logo = { ...logoRaw, image: logoImage };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Disable position / size transitions while the window is being resized so
  // the header tracks the (dynamically updated) --offer-banner-height instantly.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      setIsResizing(true);
      clearTimeout(timer);
      timer = setTimeout(() => setIsResizing(false), 200);
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const propertyAddressUrl = usePropertyAddressUrl();
  // Editor URL → hook fallback → constructed maps directions URL → '#'.
  const directionsUrl = address.url
    || propertyAddressUrl
    || (address.text ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(String(address.text))}` : '#');

  const closeMobileMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      {/* Spacer to offset content below the fixed header + offer banner */}
      <div style={{ height: `calc(${headerHeight}px + var(--offer-banner-height, 0px))`, transition: isResizing ? 'none' : 'height 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />

      <header ref={headerRef} className="fixed left-0 right-0 z-50 w-full" style={{ top: 'var(--offer-banner-height, 0px)', transition: isResizing ? 'none' : 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}>
        {/* ================================================================ */}
        {/* Utility Bar                                                      */}
        {/* ================================================================ */}
        <div className="header-utility-bar text-[var(--vibe-color-text-on-secondary)]">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-9">
              {/* Left: Address + Phone (desktop) */}
              <div className="hidden md:flex items-center divide-x divide-white/20">
                <EditableSlotGroup groupPath="header.address" definition={addressGroupDef}>
                  <a
                    href={directionsUrl}
                    target={address._target || '_blank'}
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 pr-4 text-xs tracking-wide hover:opacity-70 transition-opacity"
                  >
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{address.text}</span>
                  </a>
                </EditableSlotGroup>

                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className="flex items-center gap-1.5 pl-4 text-xs tracking-wide hover:opacity-70 transition-opacity"
                >
                  <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <EditableToken
                    path="header.phone"
                    defaultField="property_call_tracking_phone"
                    defaultValue="(555) 555-5555"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>
              </div>

              {/* Right: Utility CTA buttons (desktop) */}
              <div className="hidden md:flex items-center divide-x divide-white/20 ml-auto">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="header-utility-cta-btn px-3 py-1 text-[11px] font-semibold tracking-widest uppercase"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="header-utility-cta-btn px-3 py-1 text-[11px] font-semibold tracking-widest uppercase"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Mobile utility row: phone + directions */}
              <div className="md:hidden flex items-center justify-between w-full text-xs">
                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className="flex items-center gap-1.5 hover:opacity-70 transition-opacity"
                >
                  <PhoneIcon className="w-3.5 h-3.5" />
                  <span>{formatPhone(phone)}</span>
                </a>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:opacity-70 transition-opacity"
                >
                  <MapPinIcon className="w-3.5 h-3.5" />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Main Navigation Bar                                              */}
        {/* ================================================================ */}
        <div className="bg-white border-b border-[var(--vibe-color-border)] shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 lg:h-[72px]">
              {/* Logo — on mobile it may shrink (min-w-0 + capped width) so it
                  never overlaps the CTA/hamburger; desktop keeps its full size. */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" className="min-w-0 flex-shrink max-w-[38vw] lg:max-w-none lg:flex-shrink-0">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logoRaw.alt || 'Community Logo'}
                      sizes="200px"
                      className="h-8 lg:h-12 w-auto max-w-full object-contain"
                    />
                  ) : (
                    <div className="font-heading text-base lg:text-2xl font-bold text-black truncate">
                      The Avenue
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links — built dynamically from pages.json */}
              {/* Uses dynamic overflow detection to show "More" dropdown when needed */}
              <DesktopNav navLinks={navLinks} />

              {/* Right group: CTA Button + Mobile Hamburger — grouped so CTA hugs the hamburger */}
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className="nav-cta-btn group/cta relative inline-flex items-center gap-1.5 px-2.5 py-2 lg:gap-2 lg:px-7 lg:py-3.5 text-[10px] lg:text-[11px] font-bold tracking-[0.08em] lg:tracking-[0.15em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300 whitespace-nowrap"
                  >
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </span>
                    <svg className="relative z-10 w-3 h-3 lg:w-3.5 lg:h-3.5 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </a>
                </EditableSlotGroup>

                {/* Mobile Hamburger */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-black transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-black transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-black transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Mobile Menu Overlay                                              */}
        {/* ================================================================ */}
        <div
          className={`lg:hidden fixed inset-x-0 bg-white overflow-y-auto shadow-xl border-b border-[var(--vibe-color-border)] transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{
            top: `calc(${headerHeight}px + var(--offer-banner-height, 0px))`,
            maxHeight: `calc(100vh - ${headerHeight}px - var(--offer-banner-height, 0px))`,
          }}
        >
          {/* Navigation Items — built dynamically from pages.json, followed by
              the Apply Now and Resident Portal links. */}
          <nav role="navigation" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <MobileNavLink
                key={link.href}
                link={link}
                onNavigate={closeMobileMenu}
              />
            ))}

            <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
              <a
                href={applyNowLink.url || '/apply-now'}
                target={applyNowLink._target || '_self'}
                onClick={closeMobileMenu}
                className="block py-2.5 px-6 font-body text-[13px] font-semibold uppercase tracking-wider text-black hover:text-neutral-600 transition-colors border-b border-[var(--vibe-color-border)]/50"
              >
                {applyNowLink.label || 'Apply Now'}
              </a>
            </EditableSlotGroup>

            <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
              <a
                href={residentAccessLink.url || '/resident-access'}
                target={residentAccessLink._target || '_self'}
                onClick={closeMobileMenu}
                className="block py-2.5 px-6 font-body text-[13px] font-semibold uppercase tracking-wider text-black hover:text-neutral-600 transition-colors border-b border-[var(--vibe-color-border)]/50"
              >
                {residentAccessLink.label || 'Resident Access'}
              </a>
            </EditableSlotGroup>
          </nav>
        </div>
      </header>

      <style>{`
        .header-utility-bar {
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%);
        }
        /* Hover uses a tint derived from text-on-secondary so contrast is always
           correct regardless of how dark/light the secondary colour is. */
        .header-utility-cta-btn {
          color: var(--vibe-color-text-on-secondary);
          transition: color 0.2s ease;
        }
        .header-utility-cta-btn:hover {
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 70%, transparent);
        }
        .nav-cta-btn {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 1px 3px rgba(0,0,0,0.12),
            0 2px 8px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .nav-cta-btn:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
          transform: translateY(-1px);
        }
        .nav-cta-btn:hover::before {
          left: 100%;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
  },
  slotGroups: {
    'header.address': {
      label: 'Address',
      fields: {
        text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '' },
        url: { label: 'Map URL', type: 'url', defaultValue: '' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
